import { META_TYPES, META_TYPES_V4 } from "@cart-ui/constants/BillDetails";
import {
    VALID_RENDERING_DETAILS_TYPE,
    RENDERING_DETAILS_TYPES,
    BillDetailsTag,
} from "@cart-ui/constants/RenderingDetails";
import { isEmpty, replaceAllOccurence } from "@swiggy-private/core-ui";
import { CSSProperties } from "react";
import {
    FreeDelCommsStyling,
    CollapsableBillDetailsVariants,
    ICollapsedBillDetails,
    RenderingDetails,
    RenderingDetailsMeta,
    RenderingDetailsSubDetails,
} from "@swiggy-private/cart-types";

export const getBillItemFontStyle = ({ meta = {} }: RenderingDetails): CSSProperties => {
    const { color, bold } = meta || {};
    return {
        color: !isEmpty(color) ? color : undefined,
        fontWeight: !isEmpty(bold) ? 600 : undefined,
    };
};

export const canShowTooltip = (billDetail: RenderingDetails): boolean => {
    if (isEmpty(billDetail) || !billDetail.type) {
        return false;
    }
    const itemType = billDetail.type.toUpperCase();
    if (itemType === VALID_RENDERING_DETAILS_TYPE.DISPLAY) {
        return !isEmpty(billDetail.info_text);
    } else if (itemType === VALID_RENDERING_DETAILS_TYPE.TOOLTIPDISPLAY) {
        return !isEmpty(billDetail.sub_details);
    }
    return false;
};

export const canIntermediateTextStrikeoff = ({ meta = {} }: RenderingDetailsMeta): boolean =>
    meta?.intermediateTextStriked ?? false;

export const canValueTextStrikeoff = ({ meta = {} }: RenderingDetailsMeta): boolean => meta?.valueTextStriked ?? false;

export const getIntermediateTextFontStyles = ({ meta = {} }: RenderingDetailsMeta): CSSProperties => {
    const { intermediateTextColor } = meta || {};
    return {
        color: !isEmpty(intermediateTextColor) ? intermediateTextColor : undefined,
    };
};

export const isBillItemMetaDatatypeIsString = ({ meta = {} }: RenderingDetailsMeta): boolean => {
    const { dataType } = meta || {};
    return dataType === META_TYPES.DATATYPE.STRING;
};

export const getValueTextFontStyles = ({ meta = {} }: RenderingDetailsMeta): CSSProperties => {
    const { valueTextStyle, valueTextColor } = meta || {};
    return {
        color: !isEmpty(valueTextColor) ? valueTextColor : "#02060cbf",
        fontWeight: valueTextStyle === "bold" ? 600 : 200,
    };
};

export const getInfoTextFontStyles = (
    { meta = {} }: RenderingDetailsMeta,
    freeDelCommsStyles: FreeDelCommsStyling = {},
): CSSProperties => {
    const { infoTextColor } = meta || {};
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { infoTextColor: phInfoTextColor } = freeDelCommsStyles;
    const infoColor = !isEmpty(infoTextColor) ? infoTextColor : undefined;
    const phInfoColor = !isEmpty(phInfoTextColor) ? phInfoTextColor : undefined;
    return {
        color: infoColor || phInfoColor,
    };
};

export const getBillItemMetaWindowSize = ({ meta = {} }: RenderingDetailsMeta): string => {
    const { windowWidth } = meta || {};
    let windowSize;
    if (!isEmpty(windowWidth)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        windowSize = META_TYPES.WINDOW_WIDTH[windowWidth.toUpperCase()];
    }
    return windowSize || META_TYPES.WINDOW_WIDTH.XL;
};

export const isSuperItem = (key: string): boolean => {
    return (
        key === RENDERING_DETAILS_TYPES.EXCLUSIVE_OFFER_SWIGGY_ONE ||
        key === RENDERING_DETAILS_TYPES.POSITIVE_DELIVERY_CHARGES_EXCLUSIVE_SWIGGY_ONE ||
        key === RENDERING_DETAILS_TYPES.SUBSCRIPTION_TOTAL
    );
};

export const isOneLiteItem = (key: string): boolean => {
    return (
        key === RENDERING_DETAILS_TYPES.EXCLUSIVE_OFFER_SWIGGY_ONE_LITE ||
        key === RENDERING_DETAILS_TYPES.POSITIVE_DELIVERY_CHARGES_EXCLUSIVE_SWIGGY_ONE_LITE ||
        key === RENDERING_DETAILS_TYPES.SUBSCRIPTION_TOTAL_SWIGGY_ONE_LITE
    );
};

export const isItemValueRupee = ({ meta = {}, key = "" }: RenderingDetails): boolean => {
    const _key = key.toUpperCase();
    const validRenderingDetailsTypes = [
        RENDERING_DETAILS_TYPES.GRAND_TOTAL,
        RENDERING_DETAILS_TYPES.EXCLUSIVE_OFFER_SWIGGY_ONE,
        RENDERING_DETAILS_TYPES.EXCLUSIVE_OFFER_SWIGGY_ONE_LITE,
    ];
    return isEmpty(meta) || validRenderingDetailsTypes.includes(_key);
};

export const getBillItemMetaWindowSizeV4 = ({ meta = {} }: RenderingDetailsMeta): string => {
    const { windowWidth } = meta || {};
    let windowSize;
    if (!isEmpty(windowWidth)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        windowSize = META_TYPES_V4.WINDOW_WIDTH[windowWidth.toUpperCase()];
    }
    return windowSize || META_TYPES_V4.WINDOW_WIDTH.XXL;
};

export const getBoldTitle = (title: string, startPattern = "{{", endPattern = "}}"): string => {
    let boldTitle = replaceAllOccurence(title, startPattern, `<span style="font-weight: 600;">`);
    boldTitle = replaceAllOccurence(boldTitle, endPattern, "</span>");
    return boldTitle;
};

export const getValidDeliveryFeeKeys = () => [
    RENDERING_DETAILS_TYPES.POSITIVE_DELIVERY_CHARGES,
    RENDERING_DETAILS_TYPES.POSITIVE_DELIVERY_CHARGES_EXCLUSIVE,
    RENDERING_DETAILS_TYPES.POSITIVE_DELIVERY_CHARGES_EXCLUSIVE_SWIGGY_ONE,
    RENDERING_DETAILS_TYPES.POSITIVE_DELIVERY_CHARGES_EXCLUSIVE_SWIGGY_ONE_LITE,
    RENDERING_DETAILS_TYPES.FREE_99_STORE_DELIVERY_FEE,
];

export const getCollpasableBillDetailsAnalyticsPayload = (
    collapsedBillDetails?: ICollapsedBillDetails,
    expanded?: boolean,
    savedMsg?: string,
    tag?: string,
) => {
    return {
        toPay: collapsedBillDetails?.totalAmount,
        strikeValue: collapsedBillDetails?.strikeoffAmount,
        totalSaved: collapsedBillDetails?.savingsAmount,
        message: collapsedBillDetails?.upsellMessage,
        currentState: expanded ? "uncollapsed" : "collapsed",
        surgeApplied: collapsedBillDetails?.surgeApplied,
        ...(!isEmpty(savedMsg) ? { savedMsg } : {}),
        ...(!isEmpty(tag) ? { tag } : {}),
    };
};

export const getCollapsableBillDetailsA11yText = (
    collapsedBillDetails?: ICollapsedBillDetails,
    getSubText?: string,
): string => {
    return `${collapsedBillDetails?.title} ${
        collapsedBillDetails?.totalAmount ? collapsedBillDetails?.totalAmount : ""
    } ${getSubText ? getSubText : ""}`;
};

export const isBillNotCollapsible = (
    collapsedBillDetails?: ICollapsedBillDetails,
    collapseBillDetailXPVariant?: CollapsableBillDetailsVariants,
) =>
    isEmpty(collapsedBillDetails) ||
    isEmpty(collapsedBillDetails?.title) ||
    isEmpty(collapseBillDetailXPVariant) ||
    collapseBillDetailXPVariant === CollapsableBillDetailsVariants.DEFAULT;

/**
 * Helper function to format sub-detail value by combining intermediateText and value
 * @param subDetail - The sub-detail object containing intermediateText and value
 * @returns Formatted string combining intermediateText and value
 */
const getFormattedSubDetailValue = (subDetail: RenderingDetailsSubDetails): string => {
    return (subDetail?.intermediateText || "") + " " + (subDetail?.value || "");
};

/**
 * Flattens a nested structure of sub-details into a single key-value object.
 * Combines intermediateText and value for each sub-detail with a key.
 */
export const getFlattenedSubDetails = (subDetails: RenderingDetailsSubDetails[]): Record<string, string> => {
    const result: Record<string, string> = {};

    subDetails?.forEach((subDetail) => {
        if (subDetail?.key) {
            result[subDetail.key] = getFormattedSubDetailValue(subDetail);
        }

        subDetail?.sub_details?.forEach((nested) => {
            if (nested?.key) {
                result[nested.key] = getFormattedSubDetailValue(nested);
            }
        });
    });

    return result;
};

export const getTagFromDeliveryFeeKey = (key: string): string | undefined => {
    const upperKey = key.toUpperCase();
    switch (upperKey) {
        case RENDERING_DETAILS_TYPES.POSITIVE_DELIVERY_CHARGES_EXCLUSIVE_SWIGGY_BLACK:
            return BillDetailsTag.BLCK;
        case RENDERING_DETAILS_TYPES.FREE_99_STORE_DELIVERY_FEE:
            return BillDetailsTag._99STORE;
        case RENDERING_DETAILS_TYPES.POSITIVE_DELIVERY_CHARGES_EXCLUSIVE_SWIGGY_ONE:
            return BillDetailsTag.ONE;
        default:
            return undefined;
    }
};

export const getValidDeliveryDetailsKeys = (): string[] => [
    RENDERING_DETAILS_TYPES.DELIVERY_CHARGES_SWIGGY_ONE,
    RENDERING_DETAILS_TYPES.FREE_DELIVERY_CHARGES_99_STORE,
    RENDERING_DETAILS_TYPES.DELIVERY_CHARGES_SWIGGY_ONE_LITE,
    RENDERING_DETAILS_TYPES.DELIVERY_CHARGES_SWIGGY_ONE_BLACK,
];

export const getTagFromDeliveryDetailsKey = (key: string): string | undefined => {
    const upperKey = key.toUpperCase();
    switch (upperKey) {
        case RENDERING_DETAILS_TYPES.DELIVERY_CHARGES_SWIGGY_ONE:
            return BillDetailsTag.ONE;
        case RENDERING_DETAILS_TYPES.FREE_DELIVERY_CHARGES_99_STORE:
            return BillDetailsTag._99STORE;
        case RENDERING_DETAILS_TYPES.DELIVERY_CHARGES_SWIGGY_ONE_LITE:
            return BillDetailsTag.ONE;
        case RENDERING_DETAILS_TYPES.DELIVERY_CHARGES_SWIGGY_ONE_BLACK:
            return BillDetailsTag.BLCK;
        default:
            return undefined;
    }
};
